import { type LayoutElementProps } from '@ds/foundations/layoutProps';
import { Slot } from '@ds/foundations/Slot';
import clsx from 'clsx';
import React, { CSSProperties, forwardRef, type ReactNode } from 'react';

import styles from './Col.module.css';

export interface ColBreakpoints {
    /** Columns to span on mobile (0–599dp). Max 4. Defaults to full width (4). */
    mobile?: number;
    /** Columns to span on tablet (600–1199dp). Max 8. Cascades from mobile if omitted. Defaults to full width (8). */
    tablet?: number;
    /** Columns to span on desktop (1200dp+). Max 12. Cascades from tablet if omitted. Defaults to full width (12). */
    desktop?: number;
}

/**
 * The elements a `Grid.Col` renders as: a `div`, or an `li` inside `Grid as="ul"` / `"ol"`. Anything
 * else (a `section`, an `aside`) goes inside the column, whose content is unconstrained.
 */
export type ColElement = 'div' | 'li';

export interface ColProps extends React.HTMLAttributes<HTMLElement>, LayoutElementProps<ColElement> {
    /**
     * Number of grid columns to span at each breakpoint.
     *
     * Omitting a breakpoint cascades from the next smaller one.
     * Omitting all breakpoints defaults to full-width at every breakpoint.
     *
     * @example
     * // Half-width on desktop, full-width on mobile and tablet
     * <Grid.Col span={{ desktop: 6 }}>
     *
     * @example
     * // Responsive: stacked on mobile, side-by-side on tablet+
     * <Grid.Col span={{ mobile: 4, tablet: 4, desktop: 6 }}>
     */
    span?: ColBreakpoints;
    /**
     * Number of columns to skip before this column starts, at each breakpoint.
     * Cascades from the next smaller breakpoint when omitted.
     *
     * @example
     * // Centre a 6-column block on desktop by skipping 3 on each side
     * <Grid.Col span={{ desktop: 6 }} offset={{ desktop: 3 }}>
     */
    offset?: ColBreakpoints;
    /**
     * Start this column on a fresh row at every breakpoint, even when the previous row
     * still has free tracks that auto-placement would otherwise flow it into. Pins
     * `grid-column-start` to line 1. An `offset` at a breakpoint takes precedence there.
     *
     * @example
     * // Town / state / postcode fill half a row; phone must not join them
     * <Grid.Col span={{ desktop: 2 }}>Town</Grid.Col>
     * <Grid.Col span={{ desktop: 2 }}>State</Grid.Col>
     * <Grid.Col span={{ desktop: 2 }}>Postcode</Grid.Col>
     * <Grid.Col span={{ desktop: 3 }} newRow>Phone</Grid.Col>
     */
    newRow?: boolean;
    /**
     * Extend this column's background into the Container bleed zone on both outer edges.
     * Only meaningful on columns that sit at the outer edges of the grid.
     * Content inside stays aligned to the column track boundary.
     */
    bleed?: boolean;
    /** Extend into the Container bleed zone on the left outer edge only. */
    bleedLeft?: boolean;
    /** Extend into the Container bleed zone on the right outer edge only. */
    bleedRight?: boolean;
    children?: ReactNode;
}

const Col = forwardRef<HTMLElement, ColProps>(
    (
        {
            as: Tag = 'div',
            asChild,
            span,
            offset,
            newRow = false,
            bleed,
            bleedLeft,
            bleedRight,
            className,
            children,
            style,
            ...rest
        },
        ref
    ) => {
        // Resolve span with mobile-first cascade. Omitted breakpoints fall back to the
        // next smaller value; ultimate fallback is full-width for that breakpoint's column count.
        const spanMobile = span?.mobile ?? 4;
        const spanTablet = span?.tablet ?? span?.mobile ?? 8;
        const spanDesktop = span?.desktop ?? span?.tablet ?? span?.mobile ?? 12;

        // Resolve offset with the same cascade. Zero means auto-placement (no offset).
        const offsetMobile = offset?.mobile ?? 0;
        const offsetTablet = offset?.tablet ?? offset?.mobile ?? 0;
        const offsetDesktop = offset?.desktop ?? offset?.tablet ?? offset?.mobile ?? 0;

        // CSS custom properties drive responsive grid-column placement via Col.module.css.
        // grid-column-start: offset+1 when an offset is set, or 'auto' for normal flow.
        // grid-column-end:   span N — always relative to the resolved start.
        // newRow pins the start to line 1, which auto-flow can only satisfy on the next row
        // once anything sits before it — that is what makes it a row break.
        const start = (offset: number): string => (offset > 0 ? String(offset + 1) : newRow ? '1' : 'auto');
        const cssVars = {
            '--col-start-mobile': start(offsetMobile),
            '--col-end-mobile': `span ${spanMobile}`,
            '--col-start-tablet': start(offsetTablet),
            '--col-end-tablet': `span ${spanTablet}`,
            '--col-start-desktop': start(offsetDesktop),
            '--col-end-desktop': `span ${spanDesktop}`,
        } as CSSProperties;

        const bleedsLeft = bleed || bleedLeft;
        const bleedsRight = bleed || bleedRight;

        // asChild renders the one child element instead, with these props merged onto it (Slot).
        const Comp: React.ElementType = asChild ? Slot : Tag;

        return (
            <Comp
                ref={ref}
                className={clsx(styles.col, className)}
                style={{ ...cssVars, ...style }}
                data-bleed-left={bleedsLeft || undefined}
                data-bleed-right={bleedsRight || undefined}
                {...rest}
            >
                {children}
            </Comp>
        );
    }
);

Col.displayName = 'Grid.Col';

export default Col;
